// src/pages/ManagerLogin.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { app, db } from "../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useNavigate } from "react-router-dom";
import { getOrg } from "../utils/tenant";
import { useTheme } from "../theme/ThemeProvider";
import { useBoundStoreIdentity } from "../hooks/useBoundStore";
import { clearBinding } from "../utils/storeBinding";
import { getAuth, signOut } from "firebase/auth";

function InlineToast({ message, type = "info", onClose, styles }) {
  if (!message) return null;

  const tone =
    type === "error"
      ? styles.toastError
      : type === "success"
      ? styles.toastSuccess
      : styles.toastInfo;

  return (
    <div
      role="status"
      aria-live="polite"
      onClick={onClose}
      style={{ ...styles.toast, ...tone }}
      title="Click to dismiss"
    >
      {message}
    </div>
  );
}

const getOrgId = () => getOrg()?.orgId || localStorage.getItem("orgId") || "";

function getInitialInstallOpen() {
  return false;
}

export default function ManagerLogin() {
  const navigate = useNavigate();
  const { themeMode } = useTheme();
  const styles = useMemo(() => getStyles(themeMode), [themeMode]);

  const boundStore = useBoundStoreIdentity();
  const orgId = boundStore.orgId || getOrgId();
  const storeId = boundStore.storeId || "";
  const storeNameFromLocal =
    boundStore.storeName ||
    localStorage.getItem("storeName") ||
    localStorage.getItem("storeLabel") ||
    "Store";

  const [storeName, setStoreName] = useState(storeNameFromLocal);

  useEffect(() => {
    setStoreName(storeNameFromLocal);
  }, [storeNameFromLocal]);
  
  const [showDeviceResetConfirm, setShowDeviceResetConfirm] = useState(false);
  const [deviceResetStage, setDeviceResetStage] = useState("confirm");
  const [deviceResetPin, setDeviceResetPin] = useState("");
  const [deviceResetManagerId, setDeviceResetManagerId] = useState("");
  const [deviceResetError, setDeviceResetError] = useState("");
  const [deviceResetVerifying, setDeviceResetVerifying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [signingIn, setSigningIn] = useState(false);
  const [managers, setManagers] = useState([]);
  const [selectedManagerId, setSelectedManagerId] = useState("");
  const [toast, setToast] = useState({ message: "", type: "info" });
  const [pin, setPin] = useState("");
  const [pinView, setPinView] = useState("");
  const [now, setNow] = useState(new Date());
  const [showInstallInfo, setShowInstallInfo] = useState(getInitialInstallOpen);
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine
  );

  const revealTimerRef = useRef(null);
  const pinRef = useRef("");
  const selectedManagerIdRef = useRef("");
  const loginInFlightRef = useRef(false);
  const loginAttemptRef = useRef(0);
  const mountedRef = useRef(true);

  const showToast = useCallback(
    (message, type = "info") => setToast({ message, type }),
    []
  );

  const clearRevealTimer = () => {
    if (revealTimerRef.current) {
      clearTimeout(revealTimerRef.current);
      revealTimerRef.current = null;
    }
  };

  const maskAll = (digits) => "•".repeat(digits.length);

  const setPinSafely = (nextPin, revealLast) => {
    const p = String(nextPin || "").replace(/\D/g, "").slice(0, 6);
    pinRef.current = p;
    setPin(p);

    clearRevealTimer();

    if (!p) {
      setPinView("");
      return;
    }

    if (!revealLast) {
      setPinView(maskAll(p));
      return;
    }

    const maskedExceptLast = "•".repeat(Math.max(0, p.length - 1)) + p.slice(-1);
    setPinView(maskedExceptLast);

    revealTimerRef.current = setTimeout(() => {
      setPinView(maskAll(p));
    }, 450);
  };

  const onPinKeyDown = (e) => {
    const k = e.key;

    if (
      k === "Tab" ||
      k === "ArrowLeft" ||
      k === "ArrowRight" ||
      k === "Home" ||
      k === "End"
    ) {
      return;
    }

    if (k === "Backspace") {
      e.preventDefault();
      setPinSafely(pin.slice(0, -1), false);
      return;
    }

    if (/^\d$/.test(k)) {
      e.preventDefault();
      if (pin.length >= 6) return;
      setPinSafely(pin + k, true);
      return;
    }

    if (k.length === 1) e.preventDefault();
  };

  const onPinPaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData?.getData("text") || "").replace(/\D/g, "");
    if (!pasted) return;
    const next = (pin + pasted).slice(0, 6);
    setPinSafely(next, true);
  };

  const onPinChange = (e) => {
    const raw = String(e.target.value || "");

    if (raw.length < pinView.length) {
      setPinSafely(pin.slice(0, raw.length), false);
      return;
    }

    if (raw.length > pinView.length) {
      const last = raw[raw.length - 1];
      if (/^\d$/.test(last)) {
        if (pin.length >= 6) return;
        setPinSafely(pin + last, true);
        return;
      }

      const digits = raw.replace(/\D/g, "");
      if (digits) setPinSafely(digits.slice(0, 6), true);
    }
  };

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const updateNetworkStatus = () => setIsOnline(navigator.onLine);
    window.addEventListener("online", updateNetworkStatus);
    window.addEventListener("offline", updateNetworkStatus);
    return () => {
      window.removeEventListener("online", updateNetworkStatus);
      window.removeEventListener("offline", updateNetworkStatus);
    };
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      loginAttemptRef.current += 1;
      loginInFlightRef.current = false;
      clearRevealTimer();
    };
  }, []);

  useEffect(() => {
    selectedManagerIdRef.current = selectedManagerId;
  }, [selectedManagerId]);

  useEffect(() => {
    if (!storeId) navigate("/link", { replace: true });
  }, [storeId, navigate]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (!orgId || !storeId) return;
        const snap = await getDoc(doc(db, "orgs", orgId, "stores", storeId));
        if (!alive) return;

        if (snap.exists()) {
          const d = snap.data() || {};
          const n = (d.name || d.storeName || d.label || "").toString().trim();
          if (n) {
            setStoreName(n);
            localStorage.setItem("storeName", n);
          }
        }
      } catch {}
    })();

    return () => {
      alive = false;
    };
  }, [orgId, storeId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        if (!orgId || !storeId) { if (alive) setManagers([]); return; }
        const call = httpsCallable(getFunctions(app, "us-central1"), "listNeoKioskManagers");
        const result = (await call({ orgId, storeId }))?.data || {};
        if (alive) setManagers(Array.isArray(result.managers)
          ? result.managers.filter((manager) => manager?.pinConfigured === true && manager.id && manager.name && manager.archived !== true && manager.isArchived !== true)
          : []);
      } catch (error) {
        console.error("[Neo] Could not load the linked site's managers", error);
        if (alive) { setManagers([]); showToast("Unable to load managers. Refresh the linked device.", "error"); }
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [orgId, storeId, showToast]);

  useEffect(() => {
    if (!toast.message) return;
    const t = setTimeout(() => setToast({ message: "", type: "info" }), 3200);
    return () => clearTimeout(t);
  }, [toast]);

  const handleLogin = async (e) => {
    e.preventDefault();
    // The ref closes the gap before React paints the disabled button.
    if (loginInFlightRef.current) return;

    const managerId = selectedManagerIdRef.current || selectedManagerId;
    const submittedPin = pinRef.current || pin;
    if (!managerId) {
      showToast("Please select a manager.", "error");
      return;
    }
    if (!/^\d{6}$/.test(String(submittedPin))) {
      showToast("Enter your six-digit manager PIN.", "error");
      return;
    }
    const mgr = managers.find((m) => m.id === managerId);
    if (!mgr || mgr.pinConfigured !== true) {
      showToast("This manager's PIN is not active. Refresh the manager list or ask the administrator.", "error");
      return;
    }
    if (mgr.archived === true || mgr.isArchived === true) {
      showToast("This manager account is archived. Please ask admin to restore it.", "error");
      setPinSafely("", false);
      return;
    }

    const attempt = ++loginAttemptRef.current;
    loginInFlightRef.current = true;
    setSigningIn(true);
    setToast({ message: "", type: "info" });
    let navigationStarted = false;
    let savingSession = false;
    const previousWrites = [];
    const writeSession = (storage, key, value) => {
      previousWrites.push({ storage, key, previous: storage.getItem(key) });
      storage.setItem(key, value);
    };
    try {
      const functions = getFunctions(app, "us-central1");
      const startManagerSession = httpsCallable(functions, "startNeoManagerSession");
      const result = await startManagerSession({ orgId, storeId, managerId: mgr.id, pin: String(submittedPin || "") });
      // Back, unmount and device relinking must cancel an old login attempt.
      if (!mountedRef.current || attempt !== loginAttemptRef.current) return;
      if (localStorage.getItem("orgId") !== orgId || localStorage.getItem("storeId") !== storeId || localStorage.getItem("neo.relinkRequired") === "1") {
        showToast("This device's linked site changed. Refresh and sign in again.", "error");
        return;
      }
      const serverManagerSessionToken = String(result?.data?.sessionToken || "");
      const serverManagerSessionExpiresAt = Number(result?.data?.expiresAt || 0);
      if (!serverManagerSessionToken) throw new Error("Manager session token was not returned.");

      const nowTs = Date.now();
      const sessionPayload = {
        managerId: mgr.id, managerName: mgr.name, storeId, storeName, orgId, ts: nowTs,
        serverSessionToken: serverManagerSessionToken, serverSessionExpiresAt: serverManagerSessionExpiresAt,
      };
      const canonicalManagerSession = {
        type: "manager", name: mgr.name, managerId: mgr.id, storeId, storeName, orgId, issuedAt: nowTs,
        expiresAt: serverManagerSessionExpiresAt || (nowTs + 12 * 60 * 60 * 1000),
        serverSessionToken: serverManagerSessionToken,
      };
      const sessionJson = JSON.stringify(sessionPayload);
      const canonicalSessionJson = JSON.stringify(canonicalManagerSession);
      // Both the route guard and dashboard must see the verified session before
      // navigation. Restore prior values if a browser-storage write fails.
      savingSession = true;
      writeSession(sessionStorage, "mgrSession", sessionJson);
      writeSession(localStorage, "mgrSession", sessionJson);
      writeSession(sessionStorage, "managerSession", canonicalSessionJson);
      writeSession(localStorage, "managerSession", canonicalSessionJson);
      writeSession(localStorage, "isManager", "1");
      writeSession(localStorage, "managerId", mgr.id);
      writeSession(localStorage, "managerName", mgr.name);
      writeSession(localStorage, "managerStoreId", storeId);
      writeSession(localStorage, "managerStoreName", storeName);
      savingSession = false;

      try {
        window.dispatchEvent(new Event("storage"));
        window.dispatchEvent(new CustomEvent("neo-manager-login", { detail: sessionPayload }));
        window.dispatchEvent(new CustomEvent("neo-manager-session", { detail: canonicalManagerSession }));
      } catch {}
      navigate("/manager-dashboard", { replace: true });
      navigationStarted = true;
    } catch (error) {
      if (savingSession) {
        previousWrites.reverse().forEach(({ storage, key, previous }) => {
          try { if (previous === null) storage.removeItem(key); else storage.setItem(key, previous); } catch {}
        });
      }
      if (!mountedRef.current || attempt !== loginAttemptRef.current) return;
      console.error("[Neo] Manager sign-in failed", error);
      showToast(
        savingSession
          ? "Could not save this manager session on the device. Check browser storage and try again."
          : error?.code === "functions/permission-denied"
          ? "Incorrect PIN or manager access is not available for this store."
          : error?.code === "functions/unauthenticated"
          ? "This Neo device is not authenticated. Refresh and try again."
          : "Could not create a secure manager session. Please try again.",
        "error"
      );
      setPinSafely("", false);
    } finally {
      // Keep the successful attempt locked until the dashboard takes over.
      if (mountedRef.current && attempt === loginAttemptRef.current && !navigationStarted) {
        loginInFlightRef.current = false;
        setSigningIn(false);
      }
    }
  };

  const closeDeviceReset = () => {
    setShowDeviceResetConfirm(false);
    setDeviceResetStage("confirm");
    setDeviceResetPin("");
    setDeviceResetManagerId("");
    setDeviceResetError("");
    setDeviceResetVerifying(false);
  };

  const verifyManagerPinAndResetDevice = async () => {
    const submittedPin = String(deviceResetPin || "").replace(/\D/g, "");

    if (!deviceResetManagerId || !/^\d{6}$/.test(submittedPin)) {
      setDeviceResetError("Choose your manager name and enter a six-digit PIN.");
      return;
    }

    if (!orgId || !storeId) {
      setDeviceResetError("This device is missing its linked store identity. Reset cannot be authorised.");
      return;
    }

    setDeviceResetVerifying(true);
    setDeviceResetError("");

    try {
      const verify = httpsCallable(getFunctions(app, "us-central1"), "verifyNeoManagerDeviceResetPin");
      const validation = (await verify({ orgId, storeId, managerId:deviceResetManagerId, pin: submittedPin }))?.data;
      if (validation?.verified !== true) throw new Error("The manager PIN could not be verified.");

      // Reset immediately. On iOS installed PWAs, waiting for /link to mount
      // before clearing state left a race where old Firebase/session persistence
      // could restore District Six before the reset effect ran.
      const resetKeys = [
        "orgId", "storeId", "storeName", "storeLabel", "orgName",
        "deviceUid", "deviceType", "businessMode", "appVariant",
        "recoverySecret", "installParams",
        "linkedOrgId", "linkedStoreId", "linkedStoreName",
        "managerId", "managerName", "managerStoreId", "managerStoreName",
        "staffId", "staffName", "staffRole",
        "managerSession", "cashup_manager_session", "session_manager",
        "staffSession", "cashup_staff_session", "session_staff", "cashup.session",
        "workspacePinVerified", "deviceLinked", "deviceLinkToken", "deviceSession",
        "neo.lastLinkedBinding", "neo.lastLinkedAt",
        "neo.deviceRecoveryRequired",
      ];

      try {
        resetKeys.forEach((key) => localStorage.removeItem(key));
        clearBinding();
        // This persistent lock is deliberately written LAST.
        localStorage.setItem("neo.relinkRequired", "1");
        localStorage.setItem("neo.relinkReason", "manager-reset");
      } catch {}

      try {
        sessionStorage.clear();
      } catch {}

      try {
        await signOut(getAuth(app));
      } catch {}

      try {
        if (window.caches && typeof window.caches.keys === "function") {
          const cacheKeys = await window.caches.keys();
          await Promise.all(cacheKeys.map((key) => window.caches.delete(key)));
        }
      } catch {}

      window.location.replace("/link");
    } catch (err) {
      console.error("Device reset PIN verification failed:", err);
      setDeviceResetError(err?.code === "functions/permission-denied" ? "Incorrect manager PIN." : err?.code === "functions/resource-exhausted" ? "Too many attempts. Please try later." : "Unable to verify the manager PIN right now. Please try again.");
    } finally {
      setDeviceResetVerifying(false);
    }
  };

  const goBack = () => navigate("/", { replace: true });

  const timeText = now.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  const dateText = now.toLocaleDateString([], {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  const managerCountText = loading
    ? "Loading managers..."
    : managers.length === 1
    ? "1 manager available"
    : `${managers.length} managers available`;

  return (
    <div className="page neo-manager-login-page" style={styles.page}>
      <style>{`
        .neo-manager-login-row {
          display: grid;
          grid-template-columns: minmax(0, 1.35fr) minmax(0, 1fr) minmax(150px, .72fr);
          gap: 12px;
          align-items: start;
        }
        @media (max-width: 720px) {
          .neo-manager-login-row {
            grid-template-columns: 1fr 1fr;
            gap: 8px;
          }
          .neo-manager-login-row > :last-child { grid-column: 1 / -1; }
        }
        @media (max-width: 480px) {
          .neo-manager-login-row {
            grid-template-columns: 1fr;
            gap: 8px;
          }
          .neo-manager-login-row > :last-child { grid-column: auto; }
        }

        .neo-manager-status-strip {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
        }

        .neo-manager-capability-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 10px;
          min-height: 0;
        }

        @media (max-width: 640px) {
          .neo-manager-status-strip {
            grid-template-columns: 1fr;
          }
          .neo-manager-capability-grid {
            grid-template-columns: 1fr;
            gap: 7px;
          }
          .neo-manager-capability-card { min-height: 0 !important; }
          .neo-manager-login-page { overflow-y: auto !important; }
        }

        @media (max-height: 760px) {
          .neo-manager-hero {
            padding-top: 12px !important;
            padding-bottom: 12px !important;
          }
          .neo-manager-capability-card {
            padding-top: 10px !important;
            padding-bottom: 10px !important;
          }
          .neo-manager-capability-copy {
            display: none;
          }
          .neo-manager-footer {
            padding-top: 2px !important;
          }
        }

        @media (max-height: 650px) {
          .neo-manager-subtitle,
          .neo-manager-live-date {
            display: none;
          }
          .neo-manager-capability-grid {
            gap: 6px;
          }
        }
      `}</style>
      <div style={styles.surface}>
        <div className="neo-manager-hero" style={styles.hero}>
          <div style={styles.heroGlow} />

          <div style={styles.heroTop}>
            <div>
              <div style={styles.badge}>Neo Manager Access</div>
              <h1 style={styles.title}>
                Manager Login <span style={styles.titleAccent}>{storeName}</span>
              </h1>
              <div className="neo-manager-subtitle" style={styles.subtitle}>
                Secure owner and manager entry for store controls.
              </div>
            </div>

            <div style={styles.heroMeta}>
              <div style={styles.liveClock}>{timeText}</div>
              <div className="neo-manager-live-date" style={styles.liveDate}>{dateText}</div>
            </div>
          </div>
        </div>

        <div style={styles.card}>
          <button
            type="button"
            onClick={() => setShowInstallInfo((prev) => !prev)}
            style={styles.installToggle}
            aria-expanded={showInstallInfo}
          >
            <span>{showInstallInfo ? "Hide install help" : "Show install help"}</span>
            <span style={styles.installToggleIcon}>{showInstallInfo ? "−" : "+"}</span>
          </button>

          {showInstallInfo ? (
            <div style={styles.installPanel}>
              <div style={styles.installTitle}>Install Neo App</div>
              <div style={styles.installLine}>
                <strong>iPad / iPhone:</strong> Safari → Share → Add to Home Screen
              </div>
              <div style={styles.installLine}>
                <strong>Android:</strong> Chrome menu → Install App
              </div>
              {/* Device reset helper */}
              <div style={styles.deviceResetHelper}>
                <div style={styles.deviceResetTitle}>
                  Having trouble with this device?
                </div>

                <div style={styles.deviceResetText}>
                  If this Neo device became stuck after unlinking, you can safely reset it and return to the Linking Device page.
                </div>

                <button
                  type="button"
                  onClick={() => setShowDeviceResetConfirm(true)}
                  style={styles.deviceResetButton}
                >
                  Reset This Device
                </button>
              </div>

            </div>
          ) : null}

          {loading ? (
            <div style={styles.loadingBox}>Loading managers…</div>
          ) : (
            <form onSubmit={handleLogin} style={styles.form} aria-busy={signingIn}>
              <div className="neo-manager-login-row" style={styles.loginRow}>
                <div style={styles.loginField}>
                  <label style={styles.fieldLabel}>Manager</label>
                  <select
                    value={selectedManagerId}
                    disabled={signingIn || managers.length === 0}
                    aria-label="Manager"
                    onChange={(e) => {
                      selectedManagerIdRef.current = e.target.value;
                      setSelectedManagerId(e.target.value);
                      setPinSafely("", false);
                    }}
                    style={styles.select}
                  >
                    <option value="">Select manager…</option>
                    {managers.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={styles.loginField}>
                  <label style={styles.fieldLabel}>PIN</label>
                  <input
                    type="tel"
                    value={pinView}
                    aria-label="Manager PIN"
                    disabled={signingIn || managers.length === 0}
                    onKeyDown={onPinKeyDown}
                    onPaste={onPinPaste}
                    onChange={onPinChange}
                    placeholder="Enter PIN"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    style={styles.input}
                  />
                </div>

                <div style={styles.loginAction}>
                  <label style={styles.fieldLabel}>Login</label>
                  <button type="submit" disabled={signingIn || managers.length === 0} style={{ ...styles.btn, ...styles.btnGreen, ...(signingIn || managers.length === 0 ? { opacity: 0.72, cursor: "not-allowed" } : {}) }}>
                    {signingIn ? "Signing in…" : "Login"}
                  </button>
                  <button type="button" disabled={signingIn} onClick={goBack} style={styles.backButton}>
                    Back
                  </button>
                </div>
              </div>
              {signingIn && <p role="status" aria-live="polite" style={{ margin: "12px 0 0", fontSize: 13 }}>Verifying your PIN and opening the dashboard…</p>}
              {managers.length === 0 && <p role="status" style={{ margin: "12px 0 0", fontSize: 13 }}>No managers with a configured PIN are available for this site. Ask your administrator to set a Manager PIN.</p>}
            </form>
          )}
        </div>

        <div className="neo-manager-status-strip" style={styles.statusStrip}>
          <div style={styles.statusItem}>
            <span style={styles.statusLabel}>Site</span>
            <span style={styles.statusValue}>{storeName}</span>
          </div>
          <div style={{ ...styles.statusItem, ...styles.statusItemBorder }}>
            <span style={styles.statusLabel}>Neo</span>
            <span style={styles.statusValue}>
              <span style={{ ...styles.statusDot, ...styles.statusDotGreen }} />
              Stable
            </span>
          </div>
          <div style={{ ...styles.statusItem, ...styles.statusItemBorder }}>
            <span style={styles.statusLabel}>Network</span>
            <span style={styles.statusValue}>
              <span
                style={{
                  ...styles.statusDot,
                  ...(isOnline ? styles.statusDotGreen : styles.statusDotRed),
                }}
              />
              {isOnline ? "Online" : "Offline"}
            </span>
          </div>
        </div>

        <div className="neo-manager-capability-grid" style={styles.capabilityGrid}>
          <div className="neo-manager-capability-card" style={styles.capabilityCard}>
            <div style={styles.capabilityTop}>
              <span style={styles.capabilityEyebrow}>Accountability</span>
              <span style={styles.activeChip}>Active</span>
            </div>
            <div style={styles.capabilityTitle}>Attendance & ownership</div>
            <div className="neo-manager-capability-copy" style={styles.capabilityCopy}>
              Clear responsibility across every shift.
            </div>
          </div>

          <div className="neo-manager-capability-card" style={styles.capabilityCard}>
            <div style={styles.capabilityTop}>
              <span style={styles.capabilityEyebrow}>Workforce</span>
              <span style={styles.activeChip}>Active</span>
            </div>
            <div style={styles.capabilityTitle}>Scheduling & coverage</div>
            <div className="neo-manager-capability-copy" style={styles.capabilityCopy}>
              Keep the right people visible and aligned.
            </div>
          </div>

          <div className="neo-manager-capability-card" style={styles.capabilityCard}>
            <div style={styles.capabilityTop}>
              <span style={styles.capabilityEyebrow}>Response Intelligence</span>
              <span style={styles.activeChip}>Active</span>
            </div>
            <div style={styles.capabilityTitle}>Duties & escalation</div>
            <div className="neo-manager-capability-copy" style={styles.capabilityCopy}>
              Turn missed actions into visible response.
            </div>
          </div>
        </div>

        <footer className="neo-manager-footer" style={styles.footer}>
          <span style={styles.footerPowered}>Powered by MatrixOS</span>
          <span style={styles.footerDivider}>•</span>
          <span style={styles.footerTagline}>Operate with Clarity.</span>
        </footer>
      </div>

      <InlineToast
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ message: "", type: "info" })}
        styles={styles}
      />

      {showDeviceResetConfirm && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="neo-device-reset-title"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            display: "grid",
            placeItems: "center",
            padding: 18,
            background:
              themeMode === "dark"
                ? "rgba(2, 8, 23, 0.72)"
                : "rgba(30, 41, 59, 0.30)",
            backdropFilter: "blur(10px)",
          }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeDeviceReset();
          }}
        >
          <div
            style={{
              width: "min(480px, 100%)",
              borderRadius: 22,
              padding: 20,
              background:
                themeMode === "dark"
                  ? "linear-gradient(180deg, rgba(15,31,61,0.99), rgba(8,16,33,0.99))"
                  : "linear-gradient(180deg, #ffffff, #f7faff)",
              border:
                themeMode === "dark"
                  ? "1px solid rgba(96,165,250,0.35)"
                  : "1px solid rgba(37,99,235,0.16)",
              boxShadow:
                themeMode === "dark"
                  ? "0 24px 80px rgba(0,0,0,0.45)"
                  : "0 24px 70px rgba(36,72,120,0.22)",
              color: themeMode === "dark" ? "#fff" : "#11284a",
            }}
          >
            {deviceResetStage === "confirm" ? (
              <>
                <div
                  id="neo-device-reset-title"
                  style={{ fontSize: 20, fontWeight: 900, marginBottom: 8 }}
                >
                  Reset this Neo device?
                </div>

                <div
                  style={{
                    color: themeMode === "dark" ? "#cbd5e1" : "#435979",
                    lineHeight: 1.55,
                    fontSize: 14,
                    marginBottom: 16,
                  }}
                >
                  This will unlink this browser from <strong>{storeName}</strong>, clear its
                  local Neo sessions, and return to the Linking Device page.
                </div>

                <div
                  style={{
                    padding: "12px 14px",
                    borderRadius: 14,
                    marginBottom: 16,
                    background:
                      themeMode === "dark"
                        ? "rgba(56,189,248,0.08)"
                        : "rgba(37,99,235,0.06)",
                    border:
                      themeMode === "dark"
                        ? "1px solid rgba(56,189,248,0.18)"
                        : "1px solid rgba(37,99,235,0.12)",
                    color: themeMode === "dark" ? "#b9d9f6" : "#284a74",
                    fontSize: 13,
                    lineHeight: 1.5,
                  }}
                >
                  Manager authorisation is required before this device can be unlinked.
                  The next step asks only for a valid manager PIN belonging to this linked site,
                  so reset still works even if the manager list is not loading.
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 10,
                  }}
                >
                  <button
                    type="button"
                    onClick={closeDeviceReset}
                    style={{
                      border:
                        themeMode === "dark"
                          ? "1px solid rgba(148,163,184,0.35)"
                          : "1px solid rgba(30,64,112,0.18)",
                      borderRadius: 14,
                      padding: "12px 14px",
                      fontWeight: 800,
                      cursor: "pointer",
                      background:
                        themeMode === "dark" ? "rgba(15,23,42,0.72)" : "#f7f9fc",
                      color: themeMode === "dark" ? "#fff" : "#173252",
                    }}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setDeviceResetStage("pin");
                      setDeviceResetPin("");
                      setDeviceResetError("");
                    }}
                    style={{
                      border: "none",
                      borderRadius: 14,
                      padding: "12px 14px",
                      fontWeight: 900,
                      cursor: "pointer",
                      background: "linear-gradient(90deg,#1e90ff,#19c37d)",
                      color: "#fff",
                    }}
                  >
                    Continue
                  </button>
                </div>
              </>
            ) : (
              <>
                <div
                  id="neo-device-reset-title"
                  style={{ fontSize: 20, fontWeight: 900, marginBottom: 8 }}
                >
                  Manager PIN required
                </div>

                <div
                  style={{
                    color: themeMode === "dark" ? "#cbd5e1" : "#435979",
                    lineHeight: 1.55,
                    fontSize: 14,
                    marginBottom: 16,
                  }}
                >
                  Select an active manager assigned to <strong>{storeName}</strong> and enter their PIN.
                  Neo verifies the linked store and PIN securely.
                </div>

                <label htmlFor="device-reset-manager-name" style={{ display:"block", marginBottom:8, fontSize:12, fontWeight:900, letterSpacing:"0.08em", textTransform:"uppercase", color:themeMode === "dark" ? "#93c5fd" : "#355f93" }}>Select manager</label>
                <select id="device-reset-manager-name" value={deviceResetManagerId} onChange={(e)=>{setDeviceResetManagerId(e.target.value);setDeviceResetError("");}} style={{width:"100%",background:themeMode === "dark" ? "#152540" : "white",color:themeMode === "dark" ? "white" : "#172e4d",borderRadius:12,padding:"12px",marginBottom:14}}>
                  <option value="">Select manager…</option>
                  {managers.filter((m)=>m.pinConfigured).map((m)=><option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
                <label
                  htmlFor="device-reset-manager-pin"
                  style={{
                    display: "block",
                    marginBottom: 7,
                    fontSize: 12,
                    fontWeight: 900,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: themeMode === "dark" ? "#93c5fd" : "#355f93",
                  }}
                >
                  Manager PIN
                </label>

                <input
                  id="device-reset-manager-pin"
                  type="password"
                  inputMode="numeric"
                  autoComplete="off"
                  autoFocus
                  value={deviceResetPin}
                  onChange={(e) => {
                    setDeviceResetPin(
                      String(e.target.value || "").replace(/\D/g, "").slice(0, 6)
                    );
                    setDeviceResetError("");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !deviceResetVerifying) {
                      e.preventDefault();
                      verifyManagerPinAndResetDevice();
                    }
                  }}
                  placeholder="Enter manager PIN"
                  style={{
                    boxSizing: "border-box",
                    width: "100%",
                    borderRadius: 14,
                    padding: "13px 14px",
                    marginBottom: deviceResetError ? 8 : 16,
                    outline: "none",
                    fontSize: 16,
                    fontWeight: 700,
                    background:
                      themeMode === "dark" ? "rgba(4,13,29,0.86)" : "#ffffff",
                    color: themeMode === "dark" ? "#f8fbff" : "#102845",
                    border: deviceResetError
                      ? "1px solid rgba(244,63,94,0.72)"
                      : themeMode === "dark"
                      ? "1px solid rgba(96,165,250,0.26)"
                      : "1px solid rgba(37,99,235,0.18)",
                  }}
                />

                {deviceResetError ? (
                  <div
                    role="alert"
                    style={{
                      color: themeMode === "dark" ? "#fda4af" : "#b4233c",
                      background:
                        themeMode === "dark"
                          ? "rgba(190,24,93,0.12)"
                          : "rgba(225,29,72,0.07)",
                      border:
                        themeMode === "dark"
                          ? "1px solid rgba(244,63,94,0.22)"
                          : "1px solid rgba(225,29,72,0.14)",
                      borderRadius: 12,
                      padding: "9px 11px",
                      marginBottom: 8,
                      fontSize: 13,
                      lineHeight: 1.4,
                    }}
                  >
                    {deviceResetError}
                  </div>
                ) : null}

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 10,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setDeviceResetStage("confirm");
                      setDeviceResetPin("");
                      setDeviceResetError("");
                    }}
                    disabled={deviceResetVerifying}
                    style={{
                      border:
                        themeMode === "dark"
                          ? "1px solid rgba(148,163,184,0.35)"
                          : "1px solid rgba(30,64,112,0.18)",
                      borderRadius: 14,
                      padding: "12px 14px",
                      fontWeight: 800,
                      cursor: deviceResetVerifying ? "default" : "pointer",
                      background:
                        themeMode === "dark" ? "rgba(15,23,42,0.72)" : "#f7f9fc",
                      color: themeMode === "dark" ? "#fff" : "#173252",
                      opacity: deviceResetVerifying ? 0.6 : 1,
                    }}
                  >
                    Back
                  </button>

                  <button
                    type="button"
                    onClick={verifyManagerPinAndResetDevice}
                    disabled={deviceResetVerifying || !deviceResetManagerId || deviceResetPin.length !== 6}
                    style={{
                      border: "none",
                      borderRadius: 14,
                      padding: "12px 14px",
                      fontWeight: 900,
                      cursor:
                        deviceResetVerifying || !deviceResetPin ? "default" : "pointer",
                      background: "linear-gradient(90deg,#1e90ff,#19c37d)",
                      color: "#fff",
                      opacity: deviceResetVerifying || !deviceResetPin ? 0.55 : 1,
                    }}
                  >
                    {deviceResetVerifying ? "Verifying…" : "Verify & Reset"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

function getStyles(themeMode) {
  const isDark = themeMode === "dark";

  return {
    page: {
      height: "100dvh",
      minHeight: 0,
      overflow: "hidden",
      background: "transparent",
      padding: 0,
    },
    surface: {
      height: "100%",
      minHeight: 0,
      display: "grid",
      gridTemplateRows: "auto auto auto auto auto",
      alignContent: "start",
      gap: 10,
      padding: "8px 0 10px",
    },
    hero: {
      position: "relative",
      overflow: "hidden",
      borderRadius: 22,
      padding: "16px 20px",
      background: isDark
        ? "linear-gradient(135deg, rgba(5,17,36,0.96) 0%, rgba(5,19,44,0.98) 45%, rgba(4,14,30,0.96) 100%)"
        : "linear-gradient(135deg, #f9fbff 0%, #f3f7ff 52%, #eef4ff 100%)",
      border: isDark
        ? "1px solid rgba(83,167,255,0.16)"
        : "1px solid rgba(25,78,159,0.10)",
      boxShadow: isDark
        ? "0 24px 64px rgba(0,0,0,0.34), inset 0 1px 0 rgba(255,255,255,0.02)"
        : "0 18px 48px rgba(40,84,140,0.12), inset 0 1px 0 rgba(255,255,255,0.9)",
    },
    heroGlow: {
      position: "absolute",
      inset: "18% 34% auto auto",
      width: 260,
      height: 260,
      borderRadius: "50%",
      background: isDark
        ? "radial-gradient(circle, rgba(83,167,255,0.28) 0%, rgba(83,167,255,0.08) 36%, transparent 72%)"
        : "radial-gradient(circle, rgba(90,151,255,0.22) 0%, rgba(90,151,255,0.07) 42%, transparent 72%)",
      filter: "blur(18px)",
      pointerEvents: "none",
    },
    heroTop: {
      position: "relative",
      zIndex: 1,
      display: "flex",
      justifyContent: "space-between",
      gap: 20,
      alignItems: "flex-start",
      flexWrap: "wrap",
    },
    badge: {
      display: "inline-flex",
      alignItems: "center",
      padding: "8px 14px",
      borderRadius: 999,
      marginBottom: 14,
      fontSize: 12,
      fontWeight: 800,
      letterSpacing: "0.16em",
      textTransform: "uppercase",
      color: isDark ? "#8ec5ff" : "#3f7cff",
      background: isDark ? "rgba(19,45,87,0.78)" : "rgba(61,122,255,0.08)",
      border: isDark
        ? "1px solid rgba(83,167,255,0.22)"
        : "1px solid rgba(61,122,255,0.16)",
      boxShadow: isDark ? "0 8px 24px rgba(0,0,0,0.24)" : "none",
    },
    title: {
      margin: 0,
      fontSize: "clamp(28px, 3.4vw, 44px)",
      lineHeight: 1.02,
      fontWeight: 900,
      letterSpacing: "-0.04em",
      color: isDark ? "#f5f9ff" : "#11284a",
    },
    titleAccent: {
      color: isDark ? "#56a7ff" : "#3d7bff",
    },
    subtitle: {
      marginTop: 6,
      fontSize: 14,
      lineHeight: 1.5,
      color: isDark ? "#9db3d1" : "#6a7d96",
    },
    heroMeta: {
      display: "grid",
      gap: 8,
      justifyItems: "end",
      minWidth: 180,
    },
    themeChip: {
      appearance: "none",
      border: isDark
        ? "1px solid rgba(83,167,255,0.18)"
        : "1px solid rgba(61,122,255,0.14)",
      background: isDark ? "rgba(9,28,54,0.94)" : "rgba(255,255,255,0.82)",
      color: isDark ? "#dbeaff" : "#20416f",
      borderRadius: 999,
      padding: "10px 14px",
      fontWeight: 700,
      cursor: "pointer",
      boxShadow: isDark ? "0 12px 32px rgba(0,0,0,0.22)" : "0 10px 26px rgba(69,98,140,0.08)",
    },
    liveClock: {
      fontSize: "clamp(24px, 3.2vw, 36px)",
      fontWeight: 900,
      lineHeight: 1,
      letterSpacing: "-0.04em",
      color: isDark ? "#f5f9ff" : "#12345e",
    },
    liveDate: {
      fontSize: 15,
      color: isDark ? "#9db3d1" : "#6d8098",
    },
    card: {
      borderRadius: 20,
      padding: 14,
      background: isDark
        ? "linear-gradient(180deg, rgba(18,40,68,0.98) 0%, rgba(12,30,54,0.98) 100%)"
        : "linear-gradient(180deg, #ffffff 0%, #f7faff 100%)",
      border: isDark
        ? "1px solid rgba(123,186,255,0.22)"
        : "1px solid rgba(17,74,145,0.10)",
      boxShadow: isDark
        ? "0 26px 64px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.05), 0 0 0 1px rgba(91,160,255,0.05)"
        : "0 18px 44px rgba(52,88,141,0.10)",
    },
    installToggle: {
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      padding: "10px 14px",
      borderRadius: 16,
      border: isDark
        ? "1px solid rgba(83,167,255,0.12)"
        : "1px solid rgba(17,74,145,0.10)",
      background: isDark ? "rgba(22,42,70,0.92)" : "rgba(243,247,255,0.88)",
      color: isDark ? "#d9e9ff" : "#17345f",
      fontWeight: 800,
      cursor: "pointer",
      marginBottom: showGap(true),
    },
    installToggleIcon: {
      fontSize: 20,
      lineHeight: 1,
    },
    installPanel: {
      borderRadius: 18,
      padding: "14px 16px",
      marginTop: -6,
      marginBottom: 16,
      background: isDark ? "rgba(18,36,60,0.96)" : "rgba(246,249,255,0.96)",
      border: isDark
        ? "1px solid rgba(83,167,255,0.10)"
        : "1px solid rgba(17,74,145,0.08)",
    },
    installTitle: {
      fontSize: 14,
      fontWeight: 800,
      color: isDark ? "#f1f7ff" : "#17345f",
      marginBottom: 10,
    },
    installLine: {
      fontSize: 14,
      lineHeight: 1.55,
      color: isDark ? "#9db3d1" : "#647993",
      marginBottom: 4,
    },
    deviceResetHelper: {
      marginTop: 12,
      padding: "14px",
      borderRadius: 14,
      background: isDark
        ? "linear-gradient(180deg, rgba(12,29,55,0.92) 0%, rgba(10,24,46,0.96) 100%)"
        : "linear-gradient(180deg, #f7faff 0%, #eef4ff 100%)",
      border: isDark
        ? "1px solid rgba(83,167,255,0.22)"
        : "1px solid rgba(39,98,182,0.14)",
      boxShadow: isDark
        ? "inset 0 1px 0 rgba(255,255,255,0.03)"
        : "0 8px 22px rgba(46,83,139,0.08)",
    },
    deviceResetTitle: {
      fontWeight: 800,
      marginBottom: 6,
      color: isDark ? "#f4f8ff" : "#17345f",
    },
    deviceResetText: {
      fontSize: 13,
      lineHeight: 1.5,
      marginBottom: 12,
      color: isDark ? "#9db3d1" : "#62758f",
    },
    deviceResetButton: {
      width: "100%",
      border: "none",
      borderRadius: 12,
      padding: "12px 14px",
      fontWeight: 800,
      cursor: "pointer",
      background: "linear-gradient(90deg,#1e90ff,#19c37d)",
      color: "#ffffff",
      boxShadow: isDark
        ? "0 10px 26px rgba(0,0,0,0.20)"
        : "0 10px 24px rgba(42,95,164,0.16)",
    },
    loadingBox: {
      padding: "20px 14px",
      textAlign: "center",
      color: isDark ? "#9db3d1" : "#647993",
      fontWeight: 700,
    },
    form: {
      display: "grid",
      gap: 8,
    },
    loginRow: {
      display: "grid",
      gap: 12,
      alignItems: "start",
    },
    loginField: {
      display: "grid",
      gap: 6,
      minWidth: 0,
    },
    loginAction: {
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: 6,
      minWidth: 0,
    },
    backButton: {
      justifySelf: "end",
      minWidth: 76,
      height: 34,
      padding: "0 14px",
      borderRadius: 10,
      border: isDark
        ? "1px solid rgba(83,167,255,0.18)"
        : "1px solid rgba(17,74,145,0.12)",
      background: isDark ? "rgba(20,40,68,0.76)" : "#eef4ff",
      color: isDark ? "#cfe1f8" : "#294f7f",
      fontWeight: 800,
      fontSize: 13,
      cursor: "pointer",
    },
    infoRow: {
      display: "flex",
      gap: 10,
      flexWrap: "wrap",
      marginBottom: 4,
    },
    infoPill: {
      display: "inline-flex",
      alignItems: "center",
      padding: "8px 12px",
      borderRadius: 999,
      background: isDark ? "rgba(20,55,103,0.78)" : "rgba(53,119,255,0.10)",
      color: isDark ? "#ddecff" : "#25508c",
      border: isDark
        ? "1px solid rgba(83,167,255,0.16)"
        : "1px solid rgba(53,119,255,0.14)",
      fontSize: 13,
      fontWeight: 700,
    },
    infoPillMuted: {
      display: "inline-flex",
      alignItems: "center",
      padding: "8px 12px",
      borderRadius: 999,
      background: isDark ? "rgba(255,255,255,0.03)" : "rgba(20,52,93,0.05)",
      color: isDark ? "#a8bdd8" : "#6b7f96",
      border: isDark
        ? "1px solid rgba(255,255,255,0.05)"
        : "1px solid rgba(20,52,93,0.07)",
      fontSize: 13,
      fontWeight: 700,
    },
    fieldLabel: {
      fontSize: 14,
      fontWeight: 800,
      color: isDark ? "#f5f9ff" : "#17345f",
      marginTop: 2,
    },
    select: {
      width: "100%",
      minHeight: 52,
      padding: "0 14px",
      borderRadius: 16,
      outline: "none",
      border: isDark
        ? "1px solid rgba(83,167,255,0.16)"
        : "1px solid rgba(17,74,145,0.12)",
      background: isDark ? "rgba(20,40,68,0.98)" : "#ffffff",
      color: isDark ? "#f5f9ff" : "#143157",
      fontSize: 16,
      boxShadow: isDark ? "inset 0 1px 0 rgba(255,255,255,0.05), 0 10px 22px rgba(0,0,0,0.18)" : "none",
    },
    input: {
      width: "100%",
      minHeight: 52,
      padding: "0 14px",
      borderRadius: 16,
      outline: "none",
      border: isDark
        ? "1px solid rgba(83,167,255,0.16)"
        : "1px solid rgba(17,74,145,0.12)",
      background: isDark ? "rgba(20,40,68,0.98)" : "#ffffff",
      color: isDark ? "#f5f9ff" : "#143157",
      fontSize: 20,
      letterSpacing: 4,
      boxShadow: isDark ? "inset 0 1px 0 rgba(255,255,255,0.05), 0 10px 22px rgba(0,0,0,0.18)" : "none",
    },
    helperText: {
      fontSize: 13,
      lineHeight: 1.45,
      color: isDark ? "#8ea6c4" : "#6f8096",
      marginTop: -2,
      marginBottom: 4,
    },
    btn: {
      width: "100%",
      minHeight: 52,
      borderRadius: 14,
      border: "none",
      fontWeight: 900,
      fontSize: 17,
      cursor: "pointer",
      color: "#ffffff",
      boxShadow: isDark ? "0 16px 34px rgba(0,0,0,0.24)" : "0 14px 30px rgba(42,85,145,0.12)",
    },
    btnGreen: {
      background: "linear-gradient(180deg,#2ecb78 0%, #1ea85f 100%)",
    },
    btnBlue: {
      background: "linear-gradient(180deg,#3585ff 0%, #2267e6 100%)",
    },
    statusStrip: {
      minHeight: 46,
      overflow: "hidden",
      borderRadius: 15,
      background: isDark ? "rgba(11,29,52,0.84)" : "rgba(255,255,255,0.82)",
      border: isDark
        ? "1px solid rgba(83,167,255,0.14)"
        : "1px solid rgba(17,74,145,0.09)",
      boxShadow: isDark ? "0 12px 28px rgba(0,0,0,0.16)" : "0 10px 24px rgba(52,88,141,0.07)",
    },
    statusItem: {
      minWidth: 0,
      padding: "9px 14px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
    },
    statusItemBorder: {
      borderLeft: isDark
        ? "1px solid rgba(83,167,255,0.10)"
        : "1px solid rgba(17,74,145,0.08)",
    },
    statusLabel: {
      fontSize: 10,
      fontWeight: 900,
      letterSpacing: "0.13em",
      textTransform: "uppercase",
      color: isDark ? "#7892b4" : "#71839a",
      whiteSpace: "nowrap",
    },
    statusValue: {
      minWidth: 0,
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: 12,
      fontWeight: 850,
      color: isDark ? "#e8f2ff" : "#18385f",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    statusDot: {
      width: 7,
      height: 7,
      flex: "0 0 auto",
      borderRadius: "50%",
      boxShadow: "0 0 0 3px rgba(255,255,255,0.03)",
    },
    statusDotGreen: {
      background: "#2ecb78",
      boxShadow: "0 0 10px rgba(46,203,120,0.50)",
    },
    statusDotRed: {
      background: "#f05268",
      boxShadow: "0 0 10px rgba(240,82,104,0.44)",
    },
    capabilityGrid: {
      minHeight: 0,
      alignItems: "stretch",
      gridAutoRows: "136px",
    },
    capabilityCard: {
      minWidth: 0,
      minHeight: 0,
      height: "100%",
      padding: "14px 15px",
      borderRadius: 17,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      background: isDark
        ? "linear-gradient(145deg, rgba(15,35,62,0.90), rgba(9,25,46,0.92))"
        : "linear-gradient(145deg, #ffffff, #f5f8fd)",
      border: isDark
        ? "1px solid rgba(83,167,255,0.14)"
        : "1px solid rgba(17,74,145,0.09)",
      boxShadow: isDark ? "0 14px 30px rgba(0,0,0,0.17)" : "0 12px 26px rgba(52,88,141,0.07)",
    },
    capabilityTop: {
      minWidth: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 7,
      marginBottom: 7,
    },
    capabilityEyebrow: {
      minWidth: 0,
      fontSize: "clamp(9px, 1.1vw, 11px)",
      fontWeight: 900,
      letterSpacing: "0.10em",
      textTransform: "uppercase",
      color: isDark ? "#77b7ff" : "#3375d4",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    activeChip: {
      flex: "0 0 auto",
      padding: "3px 6px",
      borderRadius: 999,
      fontSize: 8,
      fontWeight: 900,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: isDark ? "#91f0bd" : "#147343",
      background: isDark ? "rgba(46,203,120,0.10)" : "rgba(30,168,95,0.08)",
      border: isDark
        ? "1px solid rgba(46,203,120,0.18)"
        : "1px solid rgba(30,168,95,0.14)",
    },
    capabilityTitle: {
      fontSize: "clamp(12px, 1.45vw, 15px)",
      lineHeight: 1.2,
      fontWeight: 850,
      color: isDark ? "#f1f7ff" : "#17345f",
    },
    capabilityCopy: {
      marginTop: 5,
      fontSize: "clamp(10px, 1.15vw, 12px)",
      lineHeight: 1.35,
      color: isDark ? "#8fa7c5" : "#6c7f96",
    },
    footer: {
      minHeight: 24,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      gap: 7,
      padding: "4px 8px 0",
      fontSize: 11,
      whiteSpace: "nowrap",
    },
    footerPowered: {
      fontWeight: 800,
      color: isDark ? "#8fa7c5" : "#667b95",
    },
    footerDivider: {
      color: isDark ? "#3d6eaa" : "#8aa6ca",
    },
    footerTagline: {
      fontWeight: 700,
      color: isDark ? "#68adf7" : "#3778cf",
    },
    toast: {
      position: "fixed",
      left: "50%",
      bottom: 18,
      transform: "translateX(-50%)",
      zIndex: 50,
      maxWidth: 520,
      width: "calc(100% - 32px)",
      boxShadow: "0 16px 40px rgba(16,24,40,.20)",
      padding: "12px 14px",
      borderRadius: 14,
      cursor: "pointer",
      fontWeight: 700,
      backdropFilter: "blur(12px)",
    },
    toastError: {
      background: "rgba(255,235,238,0.96)",
      color: "#9b1c1c",
      border: "1px solid rgba(155,28,28,0.12)",
    },
    toastSuccess: {
      background: "rgba(230,255,241,0.96)",
      color: "#0f6a43",
      border: "1px solid rgba(15,106,67,0.12)",
    },
    toastInfo: {
      background: isDark ? "rgba(13,31,56,0.96)" : "rgba(232,244,255,0.96)",
      color: isDark ? "#dcecff" : "#1f4f86",
      border: isDark
        ? "1px solid rgba(83,167,255,0.16)"
        : "1px solid rgba(31,79,134,0.10)",
    },
  };
}

function showGap() {
  return 16;
}
